import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, Renderer2, inject, Input, ViewChild, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { HttpRequest, HttpResponse } from '@angular/common/http'
import { AsyncActionState } from '../async-action-state'
import { ActionComponent } from '../action/action.component'
import { filter, map } from 'rxjs/operators'
import { LazyLinkOptions } from './lazy-link-options'
import { LazyLinkEvent, LazyLinkFailedEventData, LazyLinkCompletedEventData } from './lazy-link-event'
import { ActionEventData } from '../action/action-event'

export type LazyLinkElement = NgElement & WithProperties<LazyLinkComponent>

@Component({
  selector: 'gpei-lazy-link',
  templateUrl: './lazy-link.component.html',
  styleUrls: [
    './lazy-link.component.sass'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule
  ]
})
export class LazyLinkComponent extends ActionComponent {

  @Input()
  @HostBinding()
  target = '_blank'

  @Input()
  @HostBinding()
  method = 'GET'

  @Input()
  @HostBinding()
  displayMessage = 'Download'

  @Input()
  @HostBinding()
  fileName = ''

  @Input()
  @HostBinding()
  loadingMessage = 'Please wait...'

  @Input()
  @HostBinding()
  errorMessage = 'Something went wrong. Press the button to try again.'

  @Input()
  @HostBinding()
  successMessage = 'Complete'

  @Input()
  @HostBinding()
  showIcon = true

  AsyncActionState = AsyncActionState
  loadingState = false

  protected _value: File

  protected readonly STARTED_EVENT_TYPE: string = 'lazylinkstart'
  protected readonly COMPLETED_EVENT_TYPE: string = 'lazylinkcomplete'
  protected readonly FAILED_EVENT_TYPE: string = 'lazylinkfailure'
  protected readonly FINAL_STATE_DURATION = 2375
  protected readonly SUPPORTED_FORMATS = [
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'mp4', 'webm', 'ogg', 'mp3', 'wav', 'pdf', 'html', 'htm', 'txt'
  ]

  protected _renderer2 = inject(Renderer2)

  resolvedUrl: string

  @ViewChild('anchor')
  _anchorElementRef!: ElementRef<HTMLAnchorElement>

  onLinkClick(event: MouseEvent): void {
    event.preventDefault()
    this.requestStart()
  }

  /**
   * Request that the download of a file be started.
   *
   * This will emit a {@link ActionEvent} of type `lazylinkstart`.
   *
   * @param args Configure the source and method of the file that is to be downloaded.
   *
   * @returns The downloaded file.
   */
  @Input()
  @HostBinding()
  async requestStart(args?: LazyLinkOptions): Promise<File> {
    // If a request is currently active, exit
    if (this.state === AsyncActionState.ACTIVE) {
      return null
    }

    // Set up the data for the events
    let eventData: ActionEventData = {}

    // If the args do not contain a promise
    if (!(args?.fromPromise instanceof Promise)) {
      eventData.method = args?.method ?? this.method
      eventData.url = args?.url ?? this.url
      eventData.encoding = args?.encoding ?? this.formenctype
      eventData.params = args?.params ?? this._params
    }

    // Emit the event via Angular output and native DOM event
    this.started.emit(eventData)
    let continued = this.dispatchEvent(this.STARTED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      return await this.start(args)
    }

    return null
  }

  /**
   * Download a file from the provided source.
   *
   * @param args Configure the source, method of the file that is to be downloaded.
   *
   * @returns The downloaded file
   */
  @Input()
  @HostBinding()
  async start(args?: LazyLinkOptions): Promise<File> {
    // Toggle the active state
    this.state = AsyncActionState.ACTIVE

    // Get the context
    let context = !(args?.fromPromise instanceof Promise) ? args : {}

    // Get the asynchronous request
    let request = args?.fromPromise instanceof Promise ? args?.fromPromise : this.sendHttpRequest(args)

    // Await the response
    try {
      let response = await request
      this.onActionComplete(response, context)
      return response
    }

    // Catch any errors thrown while while making the HTTP request
    catch (e) {
      this.onActionError(e, context)
      return null
    }
  }

  /**
   * Lifecycle hook for when the download request is completed
   *
   * Detect the changes made to the anchor element and save the file
   *
   * @param value   The file that has been downloaded
   */
  onActionComplete(value: File, context?: LazyLinkOptions): void {
    // Set up the data for the events
    let eventData: LazyLinkCompletedEventData = { file: value }

    if (context) {
      eventData.method = context?.method ?? this.method
      eventData.url = context?.url ?? this.url
      eventData.encoding = context?.encoding ?? this.formenctype
      eventData.params = context?.params ?? this._params
    }

    // Emit the event via Angular output and native DOM event
    this.completed.emit(eventData)
    let continued = this.dispatchEvent(this.COMPLETED_EVENT_TYPE, eventData)

    if (continued) {
      this.state = AsyncActionState.COMPLETE

      // Check the file extensions in the list of browser supported ones, then open the file, otherwise save it.
      if (this.SUPPORTED_FORMATS.includes(this.fileName.split('.').pop()?.toLowerCase())) {
        this.openFile(value)
      } else {
        this.saveFile(value)
      }
    }
  }

  /**
   * Lifecycle hook for when the download request encountered an error
   *
   * Clear out any data of the file in memory and set the state of the button to `AsyncActionState.ERROR`
   *
   * @param error   The error thrown while trying to download the file.
   * @param context The context in which the download was executed.
   */
  onActionError(error: Error, context?: LazyLinkOptions): void {
    // Set up the data for the events
    let eventData: LazyLinkFailedEventData = { error }

    if (context) {
      eventData.method = context?.method ?? this.method
      eventData.url = context?.url ?? this.url
      eventData.encoding = context?.encoding ?? this.formenctype
      eventData.params = context?.params ?? this._params
    }

    // Emit the event via Angular output and native DOM event
    this.failed.emit(eventData)
    let continued = this.dispatchEvent(this.FAILED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      // Set the state to error
      this.errorMessage = 'Unable to download file. Press the button to try again.'
      this.state = AsyncActionState.ERROR

      // Reset the value of the element
      this._value = null

      // Automatically transition to the idle state
      this.scheduleReturnToIdle()
    }
  }

  // Checks if the file name is entered, if so, uses that, else use the file name given from the file headers.
  @Input()
  @HostBinding()
  getFileNameFromResponse(response: HttpResponse<unknown>): string {

    if (this.fileName == '') {
      // Regular expression to find the filename in the Content-Disposition header
      let fileNamePattern = /filename="(.*)"/
      let contentDisposition = response.headers.get('Content-Disposition')

      // Check if the pattern matches and extract the filename and store it
      if (fileNamePattern.test(contentDisposition)) {
          this.fileName = fileNamePattern.exec(contentDisposition)[1]
      }
    }

    return this.fileName
  }

  /**
   * Create an HTTP request that will be used to download a file.
   *
   * @returns     HTTP request that can be sent via Angular's {@link HttpClient}.
   */
  @Input()
  @HostBinding()
  createHttpRequest(args: LazyLinkOptions): HttpRequest<unknown> {
    return super.createHttpRequest(args).clone({ responseType: 'blob' }) as HttpRequest<unknown>
  }

  protected async sendHttpRequest(args: LazyLinkOptions): Promise<File> {
    let request = this.createHttpRequest(args)

    return this._http
      .request(request)
      .pipe(
        filter(it => it instanceof HttpResponse),
        map((it: HttpResponse<Blob>) => {
          this._value = new File([it.body], this.getFileNameFromResponse(it), { type: it.body.type })
          return this._value
        })
      )
      .toPromise()
  }

  /**
   * Dispatch a native DOM event of the provided type and with the provided data.
   *
   * @param type  The type of event emitted.
   * @param data  The initial data for the event.
   *
   * @returns     `TRUE`, else it will return `FALSE` if the event's default behaviour is prevented.
   */
  protected dispatchEvent(type: string, data: ActionEventData | LazyLinkCompletedEventData | LazyLinkFailedEventData): boolean {
    let event = new LazyLinkEvent(type, {
      bubbles: true,
      cancelable: true,
      ...data
    })
    return this._elementRef.nativeElement.dispatchEvent(event)
  }

  /**
   * Schedule the state to return to the idle state after a duration has passed.
   *
   * @param duration  The length of time in miliseconds until the state returns to {@link AsyncActionState.IDLE}.
   */
  private scheduleReturnToIdle(duration = this.FINAL_STATE_DURATION): void {
    setTimeout(() => this.state = AsyncActionState.IDLE, duration)
  }

  // Opens the file in the browser
  openFile(file: File): void {

    // Get the mimetype for the file
    let mimeType = this.getMimeTypeForExtension(this.fileName.split('.').pop()?.toLowerCase())

    if (mimeType != 'application/octet-stream') {

      // Create a new object URL for the file
      this.resolvedUrl = URL.createObjectURL(new Blob([file], { type: mimeType }))

      // Set the URL to the anchor element and open it
      if (this._anchorElementRef) {
        let anchorElement = this._anchorElementRef.nativeElement
        anchorElement.href = this.resolvedUrl
        anchorElement.target = this.target
      }

      // Open the file using the URL
      this._document.defaultView.open(this.resolvedUrl, `gpei-download-${this.fileName.replace(/\W/, '-')}`)
    } else {
      // Save the file if the file extension isnt supported
      this.saveFile(file)
    }

  }

  // Downloads the file
  saveFile(file: File): void {
    this.resolvedUrl = URL.createObjectURL(file)
    let anchor = this._anchorElementRef.nativeElement
    anchor.href = this.resolvedUrl
    anchor.download = this.fileName
    anchor.click()
  }

  @HostBinding()
  get value(): File {
    return this._value
  }

  // Determine the mimetype and add it to the file. This is required because S3 returns application/octet-stream for all files
  getMimeTypeForExtension(extension: string): string {
    let mimeTypes: { [key: string]: string } = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'ogg': 'audio/ogg',
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'pdf': 'application/pdf',
      'html': 'text/html',
      'htm': 'text/html',
      'txt': 'text/plain'
    }

    // Default to binary stream if unknown
    return mimeTypes[extension] || 'application/octet-stream'
  }

}

export const registerLazyLinkElement = registerCustomElementFactory(LazyLinkComponent)
